import { useState } from 'react';
import { Button, Icon, Modal, Notice, Panel, useToast } from '@efira/ui';

import { useOverlayToken, useRotateOverlayToken } from '../api/queries';

/**
 * Зона overlay-URL.
 *
 * Три решения, каждое - ответ на конкретный дефект у конкурента.
 *
 * 1. ССЫЛКА ПОМЕЧЕНА СЕКРЕТНОЙ, явно и на самом видном месте. У конкурента она
 *    не помечена нигде, хотя даёт доступ к оверлею.
 *
 * 2. ССЫЛКА ПО УМОЛЧАНИЮ ЗАМАСКИРОВАНА и раскрывается кнопкой. Причина
 *    практическая: эту вкладку открывают во время эфира, а эфир нередко идёт с
 *    захватом экрана - раскрытый токен уехал бы зрителям.
 *
 * 3. ТОКЕН РОТИРУЕТСЯ. У конкурента токен в ссылке совпадает с идентификатором
 *    пользователя (research/PRICING_LIMITS.md), поэтому утёкшую ссылку нельзя
 *    отозвать. Наше преимущество имеет смысл только если оно видимо - отсюда
 *    кнопка перевыпуска рядом со ссылкой, а не в настройках аккаунта.
 */
export function OverlayZone() {
  const toast = useToast();

  // Загрузка и ротация - через TanStack Query: ротация меняет то, что уже
  // показано в кэше, и раньше это чинилось вручную setState-ом в эффекте
  // (docs/DECISIONS.md, запись [2026-09-11] о блоке 4б).
  const { data, isError } = useOverlayToken();
  const rotate = useRotateOverlayToken();

  const [revealed, setRevealed] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);

  // Это ЗАПРОС, а не мутация: общий обработчик тостов его не покрывает, а
  // причина сетевого отказа пользователю ничего не даёт. Текст объясняет, что
  // делать, и это здесь правильнее причины.
  const error = isError ? 'Не удалось получить ссылку. Обновите страницу.' : null;

  function onRotate() {
    rotate.mutate(undefined, {
      onSuccess: () => {
        setRevealed(false);
        toast.show({
          tone: 'success',
          title: 'Ссылка перевыпущена',
          text: 'Старая больше не работает — замените её в OBS.',
        });
      },
      // Свой тост на ошибку НЕ ставится: общий обработчик мутаций уже
      // показывает причину, присланную сервером. Второй тост с общим текстом
      // дал бы два сообщения об одном отказе, и прочитан был бы тот, который
      // ничего не объясняет.

      onSettled: () => {
        setRotateOpen(false);
      },
    });
  }

  async function copy() {
    if (data === undefined) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.url);
      toast.show({ tone: 'success', title: 'Ссылка скопирована' });
    } catch {
      // Буфер недоступен (нет https, отказ в разрешении) - показываем ссылку,
      // чтобы её можно было скопировать руками, а не оставляем в тупике.
      setRevealed(true);
      toast.show({
        tone: 'warning',
        title: 'Браузер не дал доступ к буферу',
        text: 'Ссылка раскрыта — скопируйте её вручную.',
      });
    }
  }

  const masked = data === undefined ? '' : `${data.url.slice(0, 34)}${'•'.repeat(16)}`;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Ссылка для browser source"
        subtitle="Постоянная: не меняется между эфирами, пока вы сами её не перевыпустите"
      >
        {error !== null ? (
          <Notice tone="error" title="Ошибка" live>
            {error}
          </Notice>
        ) : null}

        <div className="flex flex-col gap-3">
          <code
            className="bg-muted rounded-lg block overflow-x-auto px-3 py-2 font-mono text-sm break-all"
            aria-label="Overlay-URL"
          >
            {data === undefined ? 'Загрузка…' : revealed ? data.url : masked}
          </code>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="s"
              disabled={data === undefined}
              onClick={() => {
                setRevealed((value) => !value);
              }}
              icon={<Icon name={revealed ? 'eyeOff' : 'eye'} />}
            >
              {revealed ? 'Скрыть' : 'Показать'}
            </Button>
            <Button
              variant="primary"
              size="s"
              disabled={data === undefined}
              onClick={() => {
                void copy();
              }}
              icon={<Icon name="copy" />}
            >
              Скопировать
            </Button>
          </div>
        </div>

        <Notice tone="warning" title="Ссылка секретная">
          Она даёт доступ к вашему оверлею. Не показывайте её в эфире, не публикуйте в чате и не
          передавайте третьим лицам. Если ссылка всё же утекла — перевыпустите её: старая перестанет
          работать сразу.
        </Notice>
      </Panel>

      <Panel title="Подключение к OBS" subtitle="Три шага — и оверлей в эфире">
        <ol className="list-decimal space-y-1 ps-5 text-sm">
          <li>В OBS добавьте источник «Браузер».</li>
          <li>Вставьте ссылку выше, размер — 1080 x 1920.</li>
          <li>Снимите галочку «Выключать источник, когда он не виден».</li>
        </ol>
        <p className="text-foreground/80 text-sm">
          Пошаговый мастер под OBS, Streamlabs и TikTok LIVE Studio появится вместе с онбордингом.
        </p>
      </Panel>

      <Panel title="Перевыпуск ссылки">
        <p className="text-foreground/80 text-sm">
          Перевыпуск нужен, если ссылка попала на оверлей или в чужие руки. Старая перестаёт
          работать немедленно, и её придётся заменить в OBS.
        </p>

        <div>
          <Button
            variant="danger"
            size="m"
            disabled={data === undefined}
            onClick={() => {
              setRotateOpen(true);
            }}
          >
            Перевыпустить ссылку
          </Button>
        </div>
      </Panel>

      <Modal
        open={rotateOpen}
        onClose={() => {
          setRotateOpen(false);
        }}
        title="Перевыпустить ссылку?"
        actions={
          <>
            <Button variant="danger-solid" size="m" loading={rotate.isPending} onClick={onRotate}>
              Перевыпустить
            </Button>
            <Button
              variant="secondary"
              size="m"
              onClick={() => {
                setRotateOpen(false);
              }}
            >
              Отмена
            </Button>
          </>
        }
      >
        Старая ссылка перестанет работать немедленно. Если сейчас идёт эфир, оверлей погаснет, пока
        вы не вставите новую ссылку в OBS.
      </Modal>
    </div>
  );
}
